import { createBrowserRouter, Navigate, useLocation, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './context/AuthContext';
import { LoadingDots } from './components/LoadingDots';
import { PublicLayout } from './layouts/PublicLayout';
import { DashboardLayout } from './layouts/DashboardLayout';
import { DocsLayout } from './layouts/DocsLayout';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { DashboardHome } from './pages/DashboardHome';
import { CertsPage } from './pages/CertsPage';
import { ConsolePage } from './pages/ConsolePage';
import { DevicesPage } from './pages/DevicesPage';
import { SettingsPage } from './pages/SettingsPage';
import { KeysPage } from './pages/KeysPage';
import { MembersPage } from './pages/MembersPage';
import { ApiReferencePage } from './pages/ApiReferencePage';
import { SdkPage } from './pages/SdkPage';
import { McpPage } from './pages/McpPage';
import { SupportPage } from './pages/SupportPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { DocsPage } from './pages/DocsPage';
import { StandardsPage } from './pages/StandardsPage';
import { MessagesPage } from './pages/MessagesPage';
import { TermsPage } from './pages/TermsPage';
import { VerifyPage } from './pages/VerifyPage';

function GuideRoute() {
  const { slug = '' } = useParams();
  const { search, hash } = useLocation();
  if (slug.endsWith('.html')) {
    const canonical = slug.slice(0, -5);
    return <Navigate to={`${canonical === 'index' ? '/docs' : `/docs/${canonical}`}${search}${hash}`} replace />;
  }
  return <DocsPage />;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingDots />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Top-level router. Public: `/login`, `/signup`, `/docs/*`. Auth-guarded
 * dashboard under `/dashboard/*` with nested pages (certs, console, devices,
 * api, mcp, settings, support). `/` redirects to `/dashboard`; unknown → 404.
 */
export const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/dashboard" replace /> },
  {
    path: '/login',
    element: (
      <PublicLayout>
        <LoginPage />
      </PublicLayout>
    ),
  },
  {
    path: '/signup',
    element: (
      <PublicLayout>
        <SignupPage />
      </PublicLayout>
    ),
  },
  {
    path: '/accept-invite',
    element: (
      <PublicLayout>
        <AcceptInvitePage />
      </PublicLayout>
    ),
  },
  {
    path: '/terms',
    element: (
      <PublicLayout>
        <TermsPage />
      </PublicLayout>
    ),
  },
  {
    path: '/verify/:orgId',
    element: (
      <PublicLayout>
        <VerifyPage />
      </PublicLayout>
    ),
  },
  {
    path: '/docs',
    element: <DocsLayout />,
    children: [
      { index: true, element: <DocsPage /> },
      { path: 'standards', element: <StandardsPage /> },
      { path: 'standards/:standard', element: <DocsPage /> },
      { path: 'messages', element: <MessagesPage /> },
      { path: ':slug', element: <GuideRoute /> },
    ],
  },
  {
    path: '/dashboard',
    element: (
      <RequireAuth>
        <DashboardLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardHome /> },
      { path: 'certs', element: <CertsPage /> },
      { path: 'console', element: <ConsolePage /> },
      { path: 'devices', element: <DevicesPage /> },
      { path: 'api', element: <ApiReferencePage /> },
      { path: 'sdk', element: <SdkPage /> },
      { path: 'mcp', element: <McpPage /> },
      { path: 'keys', element: <KeysPage /> },
      { path: 'members', element: <MembersPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'support', element: <SupportPage /> },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
